import axios from 'axios';
import { msalInstance } from '../msalInstance';
import { loginRequest, msalConfig } from '../authConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── Sign-in tokens ─────────────────────────────────────────────────────────
// OTP users keep their own token. Microsoft (Entra) users send their ID token, which lasts about an hour,
// so check its expiry and renew it before it goes stale.
const tokenExpiry = (jwt) => {
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return (payload.exp || 0) * 1000;
  } catch (e) {
    return 0;
  }
};

const withTimeout = (promise, ms = 8000) =>
  Promise.race([promise, new Promise((_, reject) => setTimeout(() => reject(new Error('token timeout')), ms))]);

async function entraIdToken(forceRefresh = false) {
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) return null;
  const silent = (force) => withTimeout(msalInstance.acquireTokenSilent({ ...loginRequest, account, forceRefresh: force }));
  let result = await silent(forceRefresh);
  if (tokenExpiry(result.idToken) < Date.now() + 2 * 60 * 1000) {
    result = await silent(true);   // cached token is stale or about to be: get a new one
  }
  return result.idToken;
}

// Forget everything this browser has stored about the Microsoft sign-in (not the one-time-code login),
// so the next sign-in starts clean, like a brand new browser window.
function clearSignInCache() {
  const clientId = (msalConfig.auth.clientId || '').toLowerCase();
  [localStorage, sessionStorage].forEach((store) => {
    try {
      Object.keys(store).forEach((k) => {
        const key = k.toLowerCase();
        if (key.startsWith('msal.') || (clientId && key.includes(clientId))) store.removeItem(k);
      });
    } catch (e) { /* storage unavailable */ }
  });
}

// Send the person through Microsoft sign-in. Never more than once every 20 seconds. If sign-in still fails
// after coming back (stored sign-in data is damaged), clear it and try again; after four tries stop and let
// the page explain, so a real fault can never loop.
function backToSignIn() {
  const now = Date.now();
  let state = {};
  try { state = JSON.parse(sessionStorage.getItem('sso-state') || '{}'); } catch (e) { /* ignore */ }
  const last = state.last || 0;
  if (now - last < 20000) return;
  const attempts = now - last < 10 * 60 * 1000 ? (state.attempts || 0) + 1 : 1;
  if (attempts > 4) return;
  try { sessionStorage.setItem('sso-state', JSON.stringify({ last: now, attempts })); } catch (e) { /* ignore */ }
  if (attempts >= 2) clearSignInCache();
  Promise.resolve(msalInstance.loginRedirect(loginRequest)).catch((e) => console.warn('Could not start sign-in', e));
}

// Errors that mean the quiet renewal cannot work any more and the person has to sign in again
const needsSignIn = (err) => {
  const text = `${err?.name || ''} ${err?.errorCode || ''} ${err?.message || ''}`.toLowerCase();
  return ['interaction_required', 'interactionrequired', 'login_required', 'consent_required', 'invalid_grant', 'no_tokens_found',
    'token timeout', 'timed_out', 'monitor_window_timeout', 'no_account'].some((k) => text.includes(k));
};

api.interceptors.request.use(async (config) => {
  // Sign-in calls happen before there is any token, so never wait for one.
  // (the call that records a Microsoft sign-in is the one /auth/ call that must carry the token)
  if ((config.url || '').startsWith('/auth/') && config.url !== '/auth/session') return config;
  if (config._retried) return config;   // a retry already carries its freshly renewed token

  const otpToken = localStorage.getItem('otp_token');
  if (otpToken) {
    config.headers.Authorization = `Bearer ${otpToken}`;
    return config;
  }
  try {
    const token = await entraIdToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch (err) {
    console.warn('Could not get a sign-in token silently', err);
    if (needsSignIn(err)) backToSignIn();
  }
  return config;
});

// A 401 on a protected call: OTP users go back to the login page; Microsoft users get a fresh
// token and one retry, and if that fails too they are sent back through Microsoft sign-in.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config || {};
    const url = config.url || '';
    if (error.response?.status === 401 && !url.startsWith('/auth/')) {
      if (localStorage.getItem('otp_token')) {
        localStorage.removeItem('otp_token');
        window.location.reload();
      } else if (!config._retried) {
        config._retried = true;
        try {
          const token = await entraIdToken(true);
          if (token) {
            config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
            return api(config);
          }
          backToSignIn();   // no Microsoft account found in the browser
        } catch (e) {
          backToSignIn();
        }
      } else {
        backToSignIn();
      }
    }
    return Promise.reject(error);
  }
);

// Patches
export const patchesAPI = {
  list: (status = null, month = null, year = null) => {
    const params = {};
    if (status) params.status = status;
    if (month) params.month = month;
    if (year) params.year = year;
    return api.get('/patches', { params });
  },
  get: (id) => api.get(`/patches/${id}`),
  create: (data) => api.post('/patches', data),
  update: (id, data) => api.patch(`/patches/${id}`, data),
  delete: (id) => api.delete(`/patches/${id}`),
  sendForApproval: () => api.post('/patches/send-for-approval'),
  approve: (id) => api.post(`/patches/${id}/approve`),
  approveAll: () => api.post('/patches/approve-all'),
  reject: (id, reason) => api.post(`/patches/${id}/reject`, { reason }),
  previewPDF: () => api.post('/patches/preview-pdf', {}, { responseType: 'blob' }),
  downloadPDF: (filename) => api.get(`/patches/download-pdf/${filename}`, { responseType: 'blob' }),
};

// Scheduled Jobs
export const scheduledJobsAPI = {
  list: () => api.get('/scheduled-jobs'),
  get: (id) => api.get(`/scheduled-jobs/${id}`),
  create: (data) => api.post('/scheduled-jobs', data),
  update: (id, data) => api.put(`/scheduled-jobs/${id}`, data),
  delete: (id) => api.delete(`/scheduled-jobs/${id}`),
  execute: (id) => api.post(`/scheduled-jobs/${id}/execute`),
  history: (id, limit = 50) => api.get(`/scheduled-jobs/${id}/history`, { params: { limit } }),
  recentHistory: (limit = 100) => api.get('/scheduled-jobs/history/recent', { params: { limit } }),
  getFromEmails: () => api.get('/scheduled-jobs/from-emails'),
};

// Alias for backwards compatibility
export const jobsAPI = scheduledJobsAPI;

// Email Logs
export const emailLogsAPI = {
  list: (params = {}) => api.get('/email-logs', { params }),
  get: (id) => api.get(`/email-logs/${id}`),
  stats: () => api.get('/email-logs/stats'),
};

// WAF Monitor
export const wafAPI = {
  list: (params = {}) => api.get('/waf', { params }),
  filters: () => api.get('/waf/filters'),
  stats: () => api.get('/waf/stats'),
};

// Customer documents (administrators only)
const docForm = (file, fields = {}) => {
  const form = new FormData();
  form.append('file', file);
  Object.entries(fields).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') form.append(k, v); });
  return form;
};
const multipart = { headers: { 'Content-Type': 'multipart/form-data' } };
export const customerDocsAPI = {
  types: () => api.get('/customer-docs/types'),
  createType: (data) => api.post('/customer-docs/types', data),
  updateType: (id, data) => api.put(`/customer-docs/types/${id}`, data),
  counts: () => api.get('/customer-docs/counts'),
  list: (customerId, includeArchived = false) => api.get(`/customer-docs/customers/${customerId}/documents`, { params: { include_archived: includeArchived } }),
  upload: (customerId, file, fields) => api.post(`/customer-docs/customers/${customerId}/documents`, docForm(file, fields), multipart),
  detail: (id) => api.get(`/customer-docs/documents/${id}`),
  newVersion: (id, file, note) => api.post(`/customer-docs/documents/${id}/versions`, docForm(file, { note }), multipart),
  update: (id, data) => api.put(`/customer-docs/documents/${id}`, data),
  download: (id, version) => api.get(`/customer-docs/documents/${id}/download`, { params: version ? { version } : {}, responseType: 'blob' }),
};

// Knowledge base
export const kbAPI = {
  stats: () => api.get('/kb/stats'),
  categories: () => api.get('/kb/categories'),
  createCategory: (data) => api.post('/kb/categories', data),
  updateCategory: (id, data) => api.put(`/kb/categories/${id}`, data),
  tags: () => api.get('/kb/tags'),
  articles: (params = {}) => api.get('/kb/articles', { params }),
  article: (id) => api.get(`/kb/articles/${id}`),
  create: (data) => api.post('/kb/articles', data),
  update: (id, data) => api.put(`/kb/articles/${id}`, data),
  version: (id, n) => api.get(`/kb/articles/${id}/versions/${n}`),
  restore: (id, n) => api.post(`/kb/articles/${id}/restore/${n}`),
  uploadAsset: (id, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/kb/articles/${id}/assets`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  asset: (id, name) => api.get(`/kb/articles/${id}/assets/${encodeURIComponent(name)}`, { responseType: 'blob' }),
};

// Backup reports
export const backupsAPI = {
  dashboard: () => api.get('/backups/dashboard'),
  weeks: () => api.get('/backups/weeks'),
  week: (week, params = {}) => api.get(`/backups/weeks/${week}`, { params }),
  startReview: (week) => api.post(`/backups/weeks/${week}/start-review`),
  completeReview: (week) => api.post(`/backups/weeks/${week}/complete-review`),
  approve: (week, comment) => api.post(`/backups/weeks/${week}/approve`, { comment }),
  returnWeek: (week, comment) => api.post(`/backups/weeks/${week}/return`, { comment }),
  createAction: (week, data) => api.post(`/backups/weeks/${week}/actions`, data),
  reassignAction: (id, assigned_to, reason) => api.post(`/backups/actions/${id}/reassign`, { assigned_to, reason }),
  addComment: (id, comment) => api.post(`/backups/actions/${id}/comments`, { comment }),
  completeAction: (id, comment) => api.post(`/backups/actions/${id}/complete`, { comment }),
  exportWeek: (week, params = {}) => api.get(`/backups/weeks/${week}/export`, { params, responseType: 'blob' }),
  audit: () => api.get('/backups/audit'),
  verifyAudit: () => api.get('/backups/audit/verify'),
  instances: () => api.get('/backups/instances'),
  createInstance: (data) => api.post('/backups/instances', data),
  rotateKey: (id) => api.post(`/backups/instances/${id}/rotate-key`),
  setActive: (id, is_active) => api.put(`/backups/instances/${id}/active`, { is_active }),
  settings: () => api.get('/backups/settings'),
  putSetting: (key, value) => api.put(`/backups/settings/${key}`, { value }),
};

// Email Templates
export const templatesAPI = {
  list: () => api.get('/email-templates'),
  get: (id) => api.get(`/email-templates/${id}`),
};

// Customers
export const customersAPI = {
  list: () => api.get('/customers'),
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  locations: () => api.get('/customers/locations'),
  listContacts: (customerId) => api.get(`/customers/${customerId}/contacts`),
  createContact: (customerId, data) => api.post(`/customers/${customerId}/contacts`, data),
  updateContact: (contactId, data) => api.put(`/customers/contacts/${contactId}`, data),
  deleteContact: (contactId) => api.delete(`/customers/contacts/${contactId}`),
};

// Environments
export const environmentsAPI = {
  list: () => api.get('/environments'),
  stats: () => api.get('/environments/stats'),
  listForCustomer: (customerId) => api.get(`/environments/customer/${customerId}`),
  create: (data) => api.post('/environments', data),
  update: (id, data) => api.put(`/environments/${id}`, data),
  delete: (id) => api.delete(`/environments/${id}`),
};

// Users
export const usersAPI = {
  list: () => api.get('/users'),
  get: (id) => api.get(`/users/${id}`),
  getByEmail: (email) => api.get(`/users/email/${encodeURIComponent(email)}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

export default api;

// System requests
export const systemRequestsAPI = {
  meta: () => api.get('/system-requests/meta'),
  list: (state = 'open', q = '') => api.get('/system-requests', { params: { state, q: q || undefined } }),
  get: (id) => api.get(`/system-requests/${id}`),
  create: (data) => api.post('/system-requests', data),
  update: (id, data) => api.put(`/system-requests/${id}`, data),
  setStatus: (id, data) => api.post(`/system-requests/${id}/status`, data),
  documents: (id) => api.get(`/system-requests/${id}/documents`),
  upload: (id, file, environment) => {
    const form = new FormData();
    form.append('file', file);
    form.append('environment', environment);
    return api.post(`/system-requests/${id}/documents`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  download: (id, docId) => api.get(`/system-requests/${id}/documents/${docId}/download`, { responseType: 'blob' }),
};

// Customer licences (administrators)
export const licensesAPI = {
  customers: (q = '', all = false) => api.get('/licenses/customers', { params: { q: q || undefined, all: all || undefined } }),
  products: () => api.get('/licenses/products'),
  forCustomer: (customerId) => api.get(`/licenses/customers/${customerId}`),
  add: (customerId, data) => api.post(`/licenses/customers/${customerId}`, data),
  update: (id, data) => api.put(`/licenses/${id}`, data),
  remove: (id) => api.delete(`/licenses/${id}`),
};

// Log sources: where each customer's logs live (administrators)
export const logSourcesAPI = {
  meta: () => api.get('/log-sources/meta'),
  list: (params = {}) => api.get('/log-sources', { params }),
  history: (params = {}) => api.get('/log-sources/history', { params }),
  add: (customerId, data) => api.post(`/log-sources/customers/${customerId}`, data),
  update: (id, data) => api.put(`/log-sources/${id}`, data),
  remove: (id) => api.delete(`/log-sources/${id}`),
  publish: () => api.post('/log-sources/publish'),
};

// Customer log files synced to S3 (administrators and support)
export const customerLogsAPI = {
  customers: () => api.get('/customer-logs/customers'),
  files: (params) => api.get('/customer-logs/files', { params }),
  file: (params) => api.get('/customer-logs/file', { params }),
  download: (params) => api.get('/customer-logs/file/download', { params, responseType: 'blob' }),
  missingEnvironment: (data) => api.post('/customer-logs/missing-environment', data),
  zip: (data) => api.post('/customer-logs/zip', data, { responseType: 'blob', timeout: 300000 }),
};

// Licensing server whitelist (IP set and allowed countries on the WAF)
export const licensingWhitelistAPI = {
  get: () => api.get('/licensing-whitelist'),
  audit: () => api.get('/licensing-whitelist/audit'),
  addIp: (data) => api.post('/licensing-whitelist/ips', data),
  removeIp: (data) => api.post('/licensing-whitelist/ips/remove', data),
  setCountries: (data) => api.put('/licensing-whitelist/countries', data),
};

// SFTP access (add a customer's IP to its region's SFTP security group)
export const sftpAccessAPI = {
  customers: () => api.get('/sftp-access/customers'),
  groups: (customerId) => api.get('/sftp-access/groups', { params: { customer_id: customerId } }),
  audit: () => api.get('/sftp-access/audit'),
  addIp: (data) => api.post('/sftp-access/ips', data),
  removeIp: (data) => api.post('/sftp-access/ips/remove', data),
};

// Server resources (CPU, memory and disk from Sumo Logic)
export const serverResourcesAPI = {
  customers: () => api.get('/server-resources/customers'),
  get: (customerCode, range) => api.get('/server-resources', { params: { customer_code: customerCode, range } }),
  instance: (customerCode, host) => api.get('/server-resources/instance', { params: { customer_code: customerCode, host } }),
};

// Shared database servers by region (Sumo metrics and backup reports)
export const dbServersAPI = {
  list: (range) => api.get('/db-servers', { params: { range } }),
};

// The URL lists in the monitor servers' Telegraf configs (administrators only)
export const monitorConfigAPI = {
  servers: () => api.get('/monitor-config/servers'),
  addServer: (name) => api.post('/monitor-config/servers', { name }),
  removeServer: (name) => api.delete(`/monitor-config/servers/${encodeURIComponent(name)}`),
  get: (kind) => api.get(`/monitor-config/${kind}`),
  check: (kind, body) => api.post(`/monitor-config/${kind}/check`, body),
  push: (kind, body) => api.post(`/monitor-config/${kind}/push`, body),
  change: (id) => api.get(`/monitor-config/changes/${id}`),
  changes: (params) => api.get('/monitor-config/changes', { params }),
};

// The default Telegraf configs for the application and database servers, and rolling them out (administrators only)
export const telegrafFleetAPI = {
  master: (group) => api.get(`/telegraf-fleet/${group}`),
  version: (group, versionId) => api.get(`/telegraf-fleet/${group}/versions/${encodeURIComponent(versionId)}`),
  checkEdit: (group, body) => api.post(`/telegraf-fleet/${group}/master/check`, body),
  saveMaster: (group, body) => api.put(`/telegraf-fleet/${group}/master`, body),
  servers: (group, refresh) => api.get(`/telegraf-fleet/${group}/servers`, { params: { refresh: refresh || undefined } }),
  checkServers: (group, body) => api.post(`/telegraf-fleet/${group}/servers/check`, body),
  startRollout: (group, body) => api.post(`/telegraf-fleet/${group}/rollout`, body),
  rollout: (id) => api.get(`/telegraf-fleet/rollouts/${id}`),
  history: (group) => api.get('/telegraf-fleet/history', { params: { group } }),
};

// VPN status (read live from AWS)
export const vpnStatusAPI = {
  get: (customerCode) => api.get('/vpn-status', { params: customerCode ? { customer_code: customerCode } : {} }),
};

// Direct Connect status (read live from AWS)
export const directConnectAPI = {
  get: (customerCode) => api.get('/direct-connect', { params: { customer_code: customerCode } }),
};

// Windows service status on a customer's server (read live through Systems Manager)
export const serverServicesAPI = {
  status: (customerCode, host, service = 'manager') => api.get('/server-services/status', { params: { customer_code: customerCode, host, service } }),
  printing: (customerCode, host) => api.get('/server-services/printing', { params: { customer_code: customerCode, host } }),
  jobs: (customerCode, host) => api.get('/server-services/jobs', { params: { customer_code: customerCode, host } }),
  jobFiles: (customerCode, host, pid) => api.get('/server-services/job-files', { params: { customer_code: customerCode, host, pid } }),
  downloadJobFile: (customerCode, host, pid, kind, name) => api.get('/server-services/download', { params: { customer_code: customerCode, host, pid, kind, name }, responseType: 'blob' }),
  jobConfig: (customerCode, host, pid) => api.get('/server-services/job-config', { params: { customer_code: customerCode, host, pid } }),
  stop: (body) => api.post('/server-services/stop', body),
  start: (body) => api.post('/server-services/start', body),
  action: (id) => api.get(`/server-services/actions/${id}`),
  audit: (params) => api.get('/server-services/audit', { params }),
  actions: (customerCode, host) => api.get('/server-services/actions', { params: { customer_code: customerCode, host } }),
};

// Customer uptime against the 99.9% commitment (probes from Sumo Logic, kept in the portal)
export const uptimeAPI = {
  status: () => api.get('/uptime/status'),
  summary: (year, month) => api.get('/uptime/summary', { params: { year, month } }),
  customer: (id, year, month) => api.get(`/uptime/customer/${id}`, { params: { year, month } }),
  history: (id) => api.get(`/uptime/history/${id}`),
  averages: (fromYear, fromMonth, toYear, toMonth) => api.get('/uptime/averages', { params: { from_year: fromYear, from_month: fromMonth, to_year: toYear, to_month: toMonth } }),
  targets: () => api.get('/uptime/targets'),
  updateTarget: (id, data) => api.put(`/uptime/targets/${id}`, data),
  maintenance: (year, month) => api.get('/uptime/maintenance', { params: year ? { year, month } : {} }),
  addMaintenance: (data) => api.post('/uptime/maintenance', data),
  removeMaintenance: (id) => api.post(`/uptime/maintenance/${id}/remove`),
  sync: (days = 2) => api.post('/uptime/sync', null, { params: { days }, timeout: 120000 }),
};

// Application log (administrators)
export const appLogsAPI = {
  list: (params = {}) => api.get('/app-logs', { params }),
};

// Login history (administrators) and sign-in recording
export const loginAPI = {
  session: () => api.post('/auth/session'),
  events: (params = {}) => api.get('/login-events', { params }),
  summary: () => api.get('/login-events/summary'),
};

// Weekly SQL Server failed login review
export const failedLoginsAPI = {
  dashboard: () => api.get('/failed-logins/dashboard'),
  weeks: () => api.get('/failed-logins/weeks'),
  week: (week, params = {}) => api.get(`/failed-logins/weeks/${week}`, { params }),
  events: (week, params = {}) => api.get(`/failed-logins/weeks/${week}/events`, { params }),
  startReview: (week) => api.post(`/failed-logins/weeks/${week}/start-review`),
  completeReview: (week) => api.post(`/failed-logins/weeks/${week}/complete-review`),
  approve: (week, comment) => api.post(`/failed-logins/weeks/${week}/approve`, { comment }),
  returnWeek: (week, comment) => api.post(`/failed-logins/weeks/${week}/return`, { comment }),
  createAction: (week, data) => api.post(`/failed-logins/weeks/${week}/actions`, data),
  reassignAction: (id, assigned_to, reason) => api.post(`/failed-logins/actions/${id}/reassign`, { assigned_to, reason }),
  addComment: (id, comment) => api.post(`/failed-logins/actions/${id}/comments`, { comment }),
  completeAction: (id, comment) => api.post(`/failed-logins/actions/${id}/complete`, { comment }),
  exportWeek: (week, params = {}) => api.get(`/failed-logins/weeks/${week}/export`, { params, responseType: 'blob' }),
};

// Reporting (usage, moved from the earlier Reporting app); open to every portal role, including Sales / Account Management
export const reportingAPI = {
  usage: (dimension) => api.get('/reporting/usage', { params: { dimension } }),
};
